/**
 * The inventory's default copy (the rule is in `shared.ts`): the vault's heading, its status
 * chips and what each says while empty, what a unit's tile says in place of its value, the line
 * over the grid, and the bar while units are picked. The header and tab row over it are the
 * account's (`account.ts`). Whether a unit can be sold and why it can't ship are the platform's
 * (`inventoryUnits.ts`), and so are the sale's confirmation and its refusals, which stay with the
 * storefront.
 */
import type { InventoryItemStatus } from "../index.js";
import { capitalizeTerm, type StorefrontTerminology, termCount } from "../storefrontTerminology.js";
import { browseBoxesLabel, STOREFRONT_PAGING_COPY } from "./shared.js";

/**
 * The inventory's tabs, by the status of the units each lists, in the order its chips show them:
 * what the shopper holds, what is on its way to them, what is gone.
 */
export const INVENTORY_TABS = [
  "owned",
  "fulfillment_requested",
  "sold",
] as const satisfies readonly InventoryItemStatus[];
export type InventoryTab = (typeof INVENTORY_TABS)[number];

/** Whether a chip's value is one of the inventory's tabs. */
export function isInventoryTab(value: string): value is InventoryTab {
  return (INVENTORY_TABS as readonly string[]).includes(value);
}

/** The inventory's own words, by section, in the store's terms. */
export interface InventoryCopy {
  vault: {
    title: string;
    description: string;
    /** The chips' accessible name, each tab's chip, and what each tab says while it's empty. */
    status: {
      label: string;
      options: Record<InventoryTab, string>;
      empty: Record<InventoryTab, string>;
    };
    /** Beside an empty Owned tab: the link to the catalog. */
    browse: string;
    /** What a unit's tile says in place of its value: sold back, shipping, shipped, worthless. */
    notes: { sold: string; shipping: string; shipped: string; noValue: string };
    /** The line over the grid: how many of those shown can be sold, and Select all or its undo. */
    selectable: {
      /** `limit` is the most one sale takes, named once more can be sold than that. */
      summary: (counts: { sellable: number; shown: number; limit: number }) => string;
      selectAll: string;
      clearSelection: string;
    };
    /** The next page, and the count line under the grid. */
    paging: { loadMore: string; showing: (shown: number) => string };
  };
  /** The bar while units are picked: how many, the total's label, Clear, and the sale. */
  selection: {
    summary: (picked: number) => string;
    totalLabel: string;
    clear: string;
    /** The action, with the total formatted. */
    sell: (amount: string) => string;
  };
}

export function defaultInventoryCopy(terms: StorefrontTerminology): InventoryCopy {
  const { box, item, inventory } = terms;
  return {
    vault: {
      title: capitalizeTerm(inventory.singular),
      description: `Everything you've claimed. Sell ${item.plural} back to the house for their value, any time.`,
      status: {
        label: `Show ${inventory.singular}`,
        options: { owned: "Owned", fulfillment_requested: "Shipping", sold: "Sold back" },
        empty: {
          owned: `Nothing here yet — open some ${box.plural} and claim what you win.`,
          fulfillment_requested: "Nothing is being shipped to you right now.",
          sold: "You haven't sold anything back yet.",
        },
      },
      browse: browseBoxesLabel(terms),
      notes: {
        sold: "Sold back",
        shipping: "Shipping to you",
        shipped: "Shipped",
        noValue: "Withdraw only",
      },
      selectable: {
        summary: ({ sellable, shown, limit }) =>
          `${sellable} of ${shown} can be sold back${sellable > limit ? `, ${limit} at a time` : ""}`,
        selectAll: "Select all",
        clearSelection: "Clear selection",
      },
      paging: STOREFRONT_PAGING_COPY,
    },
    selection: {
      summary: (picked) => `${termCount(picked, item)} selected`,
      totalLabel: "Sells back for",
      clear: "Clear",
      sell: (amount) => `Sell back · ${amount}`,
    },
  };
}
